/**
 * Datasets page component.
 */

import DatasetsTable from '@/components/datasets/DatasetsTable';
import OrderModal from '@/components/modals/OrderModal';
import { apiClient } from '@/services/apiClient';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface SearchParams {
  device_id?: string;
  regex_enabled: boolean;
  start_timestamp?: string;
  end_timestamp?: string;
  data_types?: string;
  page: number;
  per_page: number;
}

export default function DatasetsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useState<SearchParams>({
    regex_enabled: false,
    page: 1,
    per_page: 10,
  });
  const [selectedDataset, setSelectedDataset] = useState<number | null>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['datasets', searchParams],
    queryFn: async () => {
      const response = await apiClient.searchDatasets(searchParams);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data;
    },
  });

  const handleSearch = () => {
    setSearchParams((prev) => ({ ...prev, page: 1 }));
    refetch();
  };

  const handleClear = () => {
    setSearchParams({
      regex_enabled: false,
      page: 1,
      per_page: 10,
    });
  };

  const handleOrderSelected = () => {
    if (selectedDataset) {
      setShowOrderModal(true);
    } else {
      toast.error('Please select a dataset first');
    }
  };

  return (
    <div>
      <h2 className="mb-4" style={{ fontWeight: 600, color: 'var(--myant-text-primary)' }}>
        {t('datasets.availableDatasets')}
      </h2>
      <Card className="mb-4">
        <Card.Body>
          <h5 style={{ fontWeight: 600, marginBottom: '1.5rem', color: 'var(--myant-text-primary)' }}>
            {t('datasets.searchDatasets')}
          </h5>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('datasets.deviceId')}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t('datasets.deviceIdPlaceholder')}
                  value={searchParams.device_id || ''}
                  onChange={(e) =>
                    setSearchParams((prev) => ({ ...prev, device_id: e.target.value }))
                  }
                />
              </Form.Group>
              <Form.Check
                type="checkbox"
                label={t('datasets.enableRegex')}
                checked={searchParams.regex_enabled}
                onChange={(e) =>
                  setSearchParams((prev) => ({ ...prev, regex_enabled: e.target.checked }))
                }
                className="mb-3"
              />
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('datasets.dataTypes')}</Form.Label>
                <Form.Select
                  value={searchParams.data_types || ''}
                  onChange={(e) =>
                    setSearchParams((prev) => ({ ...prev, data_types: e.target.value || undefined }))
                  }
                >
                  <option value="">{t('datasets.allTypes')}</option>
                  <option value="ecg">ECG</option>
                  <option value="acc">Accelerometer</option>
                  <option value="temp">Temperature</option>
                  <option value="r-peaks">R-Peaks</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('datasets.startTime')}</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={searchParams.start_timestamp || ''}
                  onChange={(e) =>
                    setSearchParams((prev) => ({
                      ...prev,
                      start_timestamp: e.target.value || undefined,
                    }))
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('datasets.endTime')}</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={searchParams.end_timestamp || ''}
                  onChange={(e) =>
                    setSearchParams((prev) => ({
                      ...prev,
                      end_timestamp: e.target.value || undefined,
                    }))
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button variant="primary" onClick={handleSearch} className="me-2">
                {t('common.search')}
              </Button>
              <Button variant="outline-secondary" onClick={handleClear}>
                {t('common.clear')}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <div className="mb-3">
        <Button variant="primary" onClick={() => refetch()} className="me-2">
          {t('common.refresh')}
        </Button>
        <Button variant="success" onClick={handleOrderSelected}>
          {t('datasets.orderDataset')}
        </Button>
      </div>
      {isLoading ? (
        <Alert variant="info">{t('datasets.loadingDatasets')}</Alert>
      ) : (
        <DatasetsTable
          data={data}
          onSelectDataset={setSelectedDataset}
          selectedDataset={selectedDataset}
          onPageChange={(page) => setSearchParams((prev) => ({ ...prev, page }))}
        />
      )}
      {showOrderModal && selectedDataset && (
        <OrderModal
          show={showOrderModal}
          onHide={() => {
            setShowOrderModal(false);
            setSelectedDataset(null);
          }}
          datasetId={selectedDataset}
        />
      )}
    </div>
  );
}
